// client/src/pages/csgo/Imports.js

import React, { useState, useEffect } from 'react';
import { Alert, Card, Row, Col, Button, Form, Spinner, Badge, Table } from 'react-bootstrap';
import { FaSteam, FaExchangeAlt, FaDatabase, FaPlug, FaSync, FaTrash, FaKey } from 'react-icons/fa';
import useCsgo from '../../hooks/useCsgo';

const STEAM_URL =
  'https://steamcommunity.com/inventory/76561198294975584/730/16?l=english&count=2500';
const CSFLOAT_URL = 'https://csfloat.com/api/v1/me/trades?&limit=5000';

function JsonImportPanel({
  step,
  title,
  icon: Icon,
  url,
  description,
  onSubmit,
  onDirectSync,
  directLabel,
  loading,
  buttonLabel,
  renderResult
}) {
  const [text, setText] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const run = async (fn) => {
    setResult(null);
    setError(null);
    try {
      const res = await fn();
      setResult(res);
      setText('');
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <Col lg={6}>
      <Card className="border-0 shadow-sm bg-white h-100">
        <Card.Body className="p-4">
          <Card.Title className="h6 fw-bold mb-2 d-flex align-items-center">
            <Badge bg="dark" className="me-2">
              {step}
            </Badge>
            <Icon className="me-2 text-primary" /> {title}
          </Card.Title>
          <p className="small text-muted mb-2">
            {description} Colle le JSON depuis{' '}
            <a href={url} target="_blank" rel="noopener noreferrer">
              cette URL
            </a>
            .
          </p>
          {onDirectSync && (
            <div className="mb-2">
              <Button
                variant="primary"
                size="sm"
                onClick={() => run(onDirectSync)}
                disabled={loading}
              >
                {loading ? <Spinner as="span" size="sm" className="me-2" /> : null}
                {directLabel}
              </Button>
              <span className="small text-muted ms-2">— ou colle le JSON ci-dessous :</span>
            </div>
          )}
          <Form.Control
            as="textarea"
            rows={onDirectSync ? 5 : 7}
            className="font-monospace small mb-2"
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
          <Button
            variant={onDirectSync ? 'outline-primary' : 'primary'}
            size="sm"
            onClick={() => run(() => onSubmit(text))}
            disabled={loading || !text.trim()}
          >
            {loading ? <Spinner as="span" size="sm" className="me-2" /> : null}
            {buttonLabel}
          </Button>

          {error && (
            <Alert variant="danger" className="mt-3 mb-0 py-2 small">
              {error}
            </Alert>
          )}
          {result && (
            <Alert variant="success" className="mt-3 mb-0 py-2 small">
              {renderResult(result)}
            </Alert>
          )}
        </Card.Body>
      </Card>
    </Col>
  );
}

function SyncPanel({ sync, loading }) {
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handle = async () => {
    setResult(null);
    setError(null);
    try {
      setResult(await sync());
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <Col lg={12}>
      <Card className="border-0 shadow-sm bg-white">
        <Card.Body className="p-4">
          <Card.Title className="h6 fw-bold mb-2 d-flex align-items-center">
            <Badge bg="dark" className="me-2">
              3
            </Badge>
            <FaDatabase className="me-2 text-primary" /> Sync BigQuery + tag des snipes
          </Card.Title>
          <p className="small text-muted mb-2">
            Récupère les snipes depuis BigQuery, puis relie chaque item à son snipe (par float exact
            si connu, sinon par date d'acquisition) pour lui donner son{' '}
            <strong>prix d'achat</strong> et le taguer <strong>Snipe</strong>. Les items sans
            correspondance sont considérés comme play / long terme.
          </p>
          <Button variant="primary" size="sm" onClick={handle} disabled={loading}>
            {loading ? <Spinner as="span" size="sm" className="me-2" /> : null}
            Synchroniser et taguer
          </Button>

          {error && (
            <Alert variant="danger" className="mt-3 mb-0 py-2 small">
              {error}
            </Alert>
          )}
          {result && (
            <Alert variant="success" className="mt-3 mb-0 py-2 small">
              {result.fetched} snipe(s) BigQuery ({result.created} nouveaux, {result.updated} mis à
              jour) — <strong>{result.tagged} item(s) taggué(s) snipe</strong>.
            </Alert>
          )}
        </Card.Body>
      </Card>
    </Col>
  );
}

const EMPTY_PLATFORM = { name: '', apiUrl: '', headerName: 'Authorization', token: '' };

function PlatformsPanel() {
  const {
    platforms,
    fetchPlatforms,
    createPlatform,
    updatePlatform,
    deletePlatform,
    syncPlatform,
    loading
  } = useCsgo();
  const [form, setForm] = useState(EMPTY_PLATFORM);
  const [feedback, setFeedback] = useState(null); // { variant, text }

  useEffect(() => {
    fetchPlatforms();
  }, [fetchPlatforms]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.name || !form.apiUrl) return;
    try {
      await createPlatform(form);
      setForm(EMPTY_PLATFORM);
      setFeedback({ variant: 'success', text: `${form.name} ajoutée.` });
    } catch (err) {
      setFeedback({ variant: 'danger', text: err.message });
    }
  };

  const handleSync = async (p) => {
    setFeedback(null);
    try {
      const r = await syncPlatform(p.id);
      setFeedback({
        variant: 'success',
        text: `${p.name} : ${r.pages} page(s) parcourue(s), ${r.events} transaction(s), ${r.purchases} achat(s) détecté(s), ${r.matched} prix d'achat rempli(s), ${r.unmatched} sans correspondance.`
      });
    } catch (err) {
      setFeedback({ variant: 'danger', text: err.message });
    }
  };

  const handleToken = async (p) => {
    const token = window.prompt(
      `Nouveau token pour ${p.name} :\n(colle la valeur du header ${p.headerName})`,
      ''
    );
    if (token == null || token.trim() === '') return;
    try {
      await updatePlatform(p.id, { token: token.trim() });
      setFeedback({ variant: 'success', text: `Token de ${p.name} mis à jour.` });
    } catch (err) {
      setFeedback({ variant: 'danger', text: err.message });
    }
  };

  const handleDelete = async (p) => {
    if (!window.confirm(`Supprimer la plateforme ${p.name} ?`)) return;
    await deletePlatform(p.id);
  };

  return (
    <Col lg={12}>
      <Card className="border-0 shadow-sm bg-white">
        <Card.Body className="p-4">
          <Card.Title className="h6 fw-bold mb-2 d-flex align-items-center">
            <FaPlug className="me-2 text-primary" /> Plateformes externes (Skinswap, ...)
          </Card.Title>
          <p className="small text-muted mb-3">
            Le serveur interroge l'API de la plateforme avec ton token, détecte les{' '}
            <strong>achats</strong> (parseur universel, peu importe le format JSON) et remplit le{' '}
            <strong>prix d'achat</strong> des items correspondants. Les statuts restent pilotés par
            Steam/CSFloat. Token expiré ? Reconnecte-toi sur le site et re-colle-le via 🔑.
          </p>

          {feedback && (
            <Alert
              variant={feedback.variant}
              className="py-2 small"
              dismissible
              onClose={() => setFeedback(null)}
            >
              {feedback.text}
            </Alert>
          )}

          {platforms.length > 0 && (
            <Table borderless size="sm" className="align-middle mb-3">
              <thead className="text-muted text-xs text-uppercase border-bottom">
                <tr>
                  <th className="pb-2">Plateforme</th>
                  <th className="pb-2">URL</th>
                  <th className="pb-2 text-center">Token</th>
                  <th className="pb-2">Dernière synchro</th>
                  <th className="pb-2 text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {platforms.map((p) => (
                  <tr key={p.id}>
                    <td className="py-2 fw-semibold text-sm">{p.name}</td>
                    <td className="py-2 text-xs text-muted" style={{ maxWidth: 260 }}>
                      <div className="text-truncate" title={p.apiUrl}>
                        {p.apiUrl}
                      </div>
                    </td>
                    <td className="py-2 text-center">
                      <Badge
                        bg={p.hasToken ? 'success' : 'warning'}
                        text={p.hasToken ? undefined : 'dark'}
                      >
                        {p.hasToken ? 'OK' : 'manquant'}
                      </Badge>
                    </td>
                    <td className="py-2 text-xs text-muted">
                      {p.lastSyncAt
                        ? `${new Date(p.lastSyncAt).toLocaleString('fr-FR')} — ${p.lastSyncSummary || ''}`
                        : 'jamais'}
                    </td>
                    <td className="py-2 text-end text-nowrap">
                      <Button
                        variant="primary"
                        size="sm"
                        className="me-2"
                        disabled={loading.syncPlatform}
                        onClick={() => handleSync(p)}
                      >
                        {loading.syncPlatform ? (
                          <Spinner as="span" size="sm" />
                        ) : (
                          <FaSync className="me-1" />
                        )}{' '}
                        Synchro
                      </Button>
                      <Button
                        variant="link"
                        size="sm"
                        className="p-0 me-2"
                        title="Mettre à jour le token"
                        onClick={() => handleToken(p)}
                      >
                        <FaKey />
                      </Button>
                      <Button
                        variant="link"
                        size="sm"
                        className="p-0 text-danger"
                        title="Supprimer"
                        onClick={() => handleDelete(p)}
                      >
                        <FaTrash />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}

          <Form onSubmit={handleAdd}>
            <Row className="g-2">
              <Col md={2}>
                <Form.Control
                  size="sm"
                  placeholder="Nom (ex: Skinswap)"
                  value={form.name}
                  onChange={set('name')}
                  required
                />
              </Col>
              <Col md={4}>
                <Form.Control
                  size="sm"
                  placeholder="URL de l'API (ex: https://api.skinswap.com/api/user/transactions?limit=100&offset=0&type=all)"
                  value={form.apiUrl}
                  onChange={set('apiUrl')}
                  required
                />
              </Col>
              <Col md={2}>
                <Form.Control
                  size="sm"
                  placeholder="Header (Authorization)"
                  value={form.headerName}
                  onChange={set('headerName')}
                />
              </Col>
              <Col md={3}>
                <Form.Control
                  size="sm"
                  type="password"
                  placeholder="Token (optionnel)"
                  value={form.token}
                  onChange={set('token')}
                />
              </Col>
              <Col md={1}>
                <Button
                  type="submit"
                  size="sm"
                  variant="outline-primary"
                  className="w-100"
                  disabled={loading.savePlatform}
                >
                  Ajouter
                </Button>
              </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>
    </Col>
  );
}

function Imports() {
  const { importSteam, importCsfloat, syncSteam, syncCsfloat, sync, loading } = useCsgo();

  return (
    <div className="animate-fade-in pb-5">
      <h1 className="h4 mb-2 fw-bold">Imports</h1>
      <p className="text-muted mb-4">
        Workflow : <strong>1.</strong> l'inventaire Steam fait foi sur ce que tu possèdes —{' '}
        <strong>2.</strong> CSFloat confirme les ventes (par asset_id exact) ; les items sortis non
        trouvés sur CSFloat = vendus ailleurs / échangés (gérables dans l'onglet Items) —{' '}
        <strong>3.</strong> BigQuery tague les snipes et fournit leur prix d'achat.
      </p>

      <Row className="g-4 mb-4">
        <JsonImportPanel
          step="1"
          title="Inventaire Steam (possession)"
          icon={FaSteam}
          url={STEAM_URL}
          description="Colle le contexte 16 (trade-protected — visible uniquement connecté) : le serveur complète automatiquement avec le contexte 2 (public) pour couvrir l'inventaire COMPLET et marquer les « sortis »."
          onSubmit={importSteam}
          onDirectSync={syncSteam}
          directLabel="Synchroniser via l'API"
          loading={loading.import}
          buttonLabel="Importer le JSON collé"
          renderResult={(r) =>
            r.pastedContexts
              ? `${r.assets} asset(s) (collé : ctx ${r.pastedContexts.join('+') || '?'}${r.fetchedContexts.length ? ` · récupéré par le serveur : ctx ${r.fetchedContexts.join('+')}` : ''}) — ${r.added} ajouté(s), ${r.present} présent(s), ${r.gone} sorti(s)${r.complete ? '' : ' ⚠️ inventaire partiel : sorties non marquées'}.`
              : `${r.assets} asset(s) (${r.contexts} contexte(s) Steam interrogés directement) — ${r.added} ajouté(s), ${r.present} présent(s), ${r.gone} sorti(s).`
          }
        />
        <JsonImportPanel
          step="2"
          title="Ventes CSFloat"
          icon={FaExchangeAlt}
          url={CSFLOAT_URL}
          description="Marque vendus les items trouvés dans tes ventes CSFloat (prix réel + float)."
          onSubmit={importCsfloat}
          onDirectSync={syncCsfloat}
          directLabel="Synchroniser via l'API"
          loading={loading.import}
          buttonLabel="Importer le JSON collé"
          renderResult={(r) =>
            `${r.pages != null ? `${r.pages} page(s), ${r.trades} trade(s) — ` : ''}${r.matched} item(s) marqué(s) vendu(s), ${r.created} créé(s) depuis l'historique.`
          }
        />
      </Row>

      <Row className="g-4 mb-4">
        <SyncPanel sync={sync} loading={loading.sync} />
      </Row>

      <Row className="g-4">
        <PlatformsPanel />
      </Row>
    </div>
  );
}

export default Imports;
